import {useMemo, useState, useEffect} from 'react';

import {format} from 'date-fns';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';

import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {
  DataTable,
  useDataTable,
  useResetPageOnChange,
  type Column,
} from '@common-web/components/data-table';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {ExportCsvButton} from '@common-web/components/export-csv-button';

import {
  Plus,
  FileText,
  Clock,
  CheckCircle,
  RefreshCw,
  Eye,
  Calendar,
  Building,
  TableProperties,
  SquareKanban,
  Search,
  AlertTriangle,
} from 'lucide-react';

import {
  getCompanyName,
  getRfcStatusDisplayName,
  getStatusColor,
  getStatusIcon,
  RFC_WORKFLOW_STATUSES,
  RfcStatusBadge,
} from '@common-web/components/rfc/RfcStatus';

import {useAuthStore} from '@common-web/stores/authStore';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';

import {RouterOutput, trpc} from '@common-web/lib/trpc';

import {RfcStatus, getActionRequiredRfcStatuses, type RfcActorRole} from '@common/types/rfc';

// Pseudo-status option in the status filter that expands into the
// actor-role-specific set of statuses requiring action.
const ACTION_REQUIRED = 'ACTION_REQUIRED';

type RfcRow = RouterOutput['clients']['rfc']['list']['data'][number];
type Filters = {status: string; client: string};

const KANBAN_STATUSES: RfcStatus[] = RFC_WORKFLOW_STATUSES;

const FILTERABLE_STATUSES: RfcStatus[] = [...KANBAN_STATUSES, RfcStatus.ARCHIVED];

export function ClientRfcDashboard({
  embedded = false,
  brokerScope,
  brokerAssignedBrokerId,
  showBrokerColumn = false,
}: {
  embedded?: boolean;
  /** Broker visibility scope ('team' includes the broker's downline). */
  brokerScope?: 'mine' | 'team';
  /** When in team scope, narrow to a single downline broker. */
  brokerAssignedBrokerId?: string | null;
  /** Show the Assigned Broker column (broker team view). */
  showBrokerColumn?: boolean;
}) {
  const navigate = useNavigate();
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const isBrokerUser = useAuthStore((state) => state.isBrokerUser());
  const isRfcAdmin = useHasPermission(CODE_PERMISSIONS.rfc.cbsQueue.key);

  const [searchParams, setSearchParams] = useSearchParams();

  // Prefixed URL keys so this table can coexist with the prospects/archive
  // tables when embedded in the pipeline dashboard tabs.
  const table = useDataTable<Filters>({
    defaultPageSize: 20,
    defaultFilters: {status: '', client: ''},
    searchDebounce: 900,
    syncToUrl: 'deals',
  });

  const statusFilter = table.filterValues.status;
  const clientFilter = table.filterValues.client;

  // Deep link from the Action Required banner pre-applies the filter. The
  // banner lives on this page, so clicking it only updates the query string
  // (no remount) — translate the param into the table's status filter and
  // drop it so a later banner click re-triggers.
  useEffect(() => {
    if (searchParams.get('filter') === 'action-required') {
      table.setFilter('status', ACTION_REQUIRED);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('filter');
          return next;
        },
        {replace: true},
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const effectiveViewMode = statusFilter === RfcStatus.ARCHIVED ? 'table' : viewMode;
  const pageSize = effectiveViewMode === 'kanban' ? 200 : table.pageSize;

  const actorRole: RfcActorRole = isBrokerUser ? 'broker' : isInternalUser ? 'internal' : 'client';
  const actionRequiredStatuses = getActionRequiredRfcStatuses(actorRole);

  // A single status ('' → no filter), or the "Action Required" preset which
  // sends the multi-status set awaiting the caller's action.
  const statusForQuery = !statusFilter
    ? undefined
    : statusFilter === ACTION_REQUIRED
      ? actionRequiredStatuses
      : (statusFilter as RfcStatus);

  const rfcsQuery = trpc.clients.rfc.list.useQuery({
    status: statusForQuery,
    search: table.debouncedSearch || undefined,
    page: table.page,
    limit: pageSize,
    clientId: isInternalUser && clientFilter ? clientFilter : undefined,
    scope: brokerScope,
    assignedBrokerId: brokerAssignedBrokerId ?? undefined,
  });

  const clientsQuery = trpc.internal.client.list.useQuery(undefined, {
    enabled: isInternalUser,
    refetchOnWindowFocus: false,
  });

  const exportCsvQuery = trpc.clients.rfc.exportCsvForDeals.useQuery(
    {
      status:
        !statusFilter || statusFilter === ACTION_REQUIRED ? undefined : (statusFilter as RfcStatus),
      statuses: statusFilter === ACTION_REQUIRED ? actionRequiredStatuses : undefined,
      search: table.debouncedSearch || undefined,
      clientId: isInternalUser && clientFilter ? clientFilter : undefined,
      scope: brokerScope,
      assignedBrokerId: brokerAssignedBrokerId ?? undefined,
    },
    {enabled: false},
  );

  // Filter/search changes reset the page inside useDataTable; view-mode and
  // broker-scope changes live outside it.
  useResetPageOnChange(`${viewMode}|${brokerScope ?? ''}|${brokerAssignedBrokerId ?? ''}`, table);

  const rfcData = rfcsQuery.data?.data || [];
  const pagination = rfcsQuery.data?.pagination;
  const statusCounts = rfcsQuery.data?.statusCounts;

  const groupedByStatus = useMemo(() => {
    const groups: Record<string, typeof rfcData> = {};
    KANBAN_STATUSES.forEach((s) => (groups[s] = []));
    rfcData.forEach((item) => {
      if (!item.status || !groups[item.status]) return;
      groups[item.status].push(item);
    });
    return groups;
  }, [rfcData]);

  // User-type specific config
  const pageTitle = isInternalUser ? 'Deals Administration' : isBrokerUser ? 'My Deals' : 'Deals';
  const pageDescription = isBrokerUser
    ? 'Manage your assigned deals'
    : 'Manage deal submissions and processing';
  const createUrl = isInternalUser
    ? '/rfc/create'
    : isBrokerUser
      ? '/broker/deals/new'
      : '/rfc/new';

  const getDetailUrl = (rfc: {id: string}) => {
    if (isInternalUser) return `/rfc/admin/${rfc.id}`;
    if (isBrokerUser) return `/broker/deals/${rfc.id}`;
    return `/rfc/review/${rfc.id}`;
  };

  const getViewUrl = (rfc: {accessToken: string}) => `/rfc/${rfc.accessToken}/view`;

  // Metric calculations
  const pendingCount = isBrokerUser
    ? statusCounts?.BROKER_REVIEW || 0
    : isInternalUser
      ? (statusCounts?.SUBMITTED || 0) + (statusCounts?.CLIENT_REVIEW || 0)
      : (statusCounts?.SUBMITTED || 0) +
        (statusCounts?.CLIENT_REVIEW || 0) +
        (statusCounts?.BROKER_REVIEW || 0);

  const cbsQueueCount = isRfcAdmin
    ? (statusCounts?.CLIENT_APPROVED || 0) + (statusCounts?.MSA_SIGNED || 0)
    : 0;

  const processingCount = isBrokerUser
    ? (statusCounts?.BROKER_APPROVED || 0) +
      (statusCounts?.CLIENT_REVIEW || 0) +
      (statusCounts?.MSA_PENDING || 0) +
      (statusCounts?.MSA_SENT || 0)
    : isInternalUser
      ? (statusCounts?.CLIENT_APPROVED || 0) + (statusCounts?.CBS_PROCESSING || 0)
      : (statusCounts?.CLIENT_APPROVED || 0) +
        (statusCounts?.CBS_PROCESSING || 0) +
        (statusCounts?.MSA_PENDING || 0) +
        (statusCounts?.MSA_SENT || 0) +
        (statusCounts?.MSA_SIGNED || 0);

  const hasActiveFilters =
    !!table.debouncedSearch || !!statusFilter || (isInternalUser && !!clientFilter);

  const columns: Column<RfcRow>[] = [
    {
      id: 'company',
      header: isInternalUser ? 'Client & Company' : 'Company',
      render: (rfc) => (
        <div className='flex items-center space-x-3'>
          <Building className='h-4 w-4 text-brand-gray' />
          <div>
            <p className='font-medium text-brand-navy'>{getCompanyName(rfc)}</p>
            {isInternalUser && <p className='text-sm text-brand-gray'>{rfc.client.name}</p>}
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (rfc) => <RfcStatusBadge status={rfc.status as RfcStatus} />,
    },
    ...(showBrokerColumn
      ? [
          {
            id: 'assignedBroker',
            header: 'Assigned Broker',
            render: (rfc) => (
              <span className='text-sm text-brand-navy'>
                {rfc.assignedBroker?.name || '\u2014'}
              </span>
            ),
          } satisfies Column<RfcRow>,
        ]
      : []),
    {
      id: 'submitted',
      header: 'Submitted',
      render: (rfc) => (
        <div className='flex items-center space-x-2 text-sm text-brand-gray'>
          <Calendar className='h-3 w-3' />
          <span>
            {rfc.submittedAt ? format(new Date(rfc.submittedAt), 'MMM d, yyyy') : 'Not submitted'}
          </span>
        </div>
      ),
    },
    ...(isInternalUser
      ? [
          {
            id: 'attachments',
            header: 'Attachments',
            render: (rfc) => (
              <span className='text-sm text-brand-gray'>{rfc.attachments.length} files</span>
            ),
          } satisfies Column<RfcRow>,
        ]
      : []),
    {
      id: 'actions',
      header: 'Actions',
      render: (rfc) => (
        <div className='flex space-x-2'>
          {!isBrokerUser && !isInternalUser && (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => navigate(getViewUrl(rfc))}
              className='transition-colors'
            >
              <Eye className='h-4 w-4 mr-1' />
              View
            </Button>
          )}
          <Button
            variant='outline'
            size='sm'
            onClick={() => navigate(getDetailUrl(rfc))}
            className='transition-colors'
          >
            {isInternalUser ? (
              <>
                <Eye className='h-4 w-4 mr-1' />
                Manage
              </>
            ) : (
              'Review'
            )}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div
      className={
        embedded ? '' : 'min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'
      }
    >
      <div className='flex flex-col gap-0'>
        {/* Header */}
        {!embedded && (
          <PageHeader
            title={pageTitle}
            description={pageDescription}
            icon={<FileText className='h-6 w-6' />}
            isFetching={rfcsQuery.isFetching}
            isLoading={rfcsQuery.isLoading}
          >
            <div className='hidden sm:flex rounded-md border border-white/20 bg-white/10 p-0.5'>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => setViewMode('table')}
                className={
                  effectiveViewMode === 'table'
                    ? 'bg-white text-brand-navy'
                    : 'text-white hover:bg-white/10'
                }
              >
                <TableProperties className='w-full h-full' />
              </Button>
              <Button
                variant='ghost'
                size='icon'
                disabled={statusFilter === RfcStatus.ARCHIVED}
                onClick={() => setViewMode('kanban')}
                className={
                  effectiveViewMode === 'kanban'
                    ? 'bg-white text-brand-navy'
                    : 'text-white hover:bg-white/10'
                }
              >
                <SquareKanban className='w-full h-full' />
              </Button>
            </div>
            <Link to={createUrl}>
              <PageHeaderAction>
                <Plus className='h-4 w-4 mr-2' />
                Create New Deal
              </PageHeaderAction>
            </Link>
          </PageHeader>
        )}

        {/* Main Content */}
        <div className={embedded ? '' : 'px-8 py-6 max-w-7xl mx-auto w-full'}>
          {/* Metric Cards */}
          <div
            className={`grid grid-cols-2 ${isInternalUser ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4 mb-6`}
          >
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                  {isBrokerUser ? 'Needs Review' : 'Pending Review'}
                  <Clock className='h-5 w-5 text-amber' />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rfcsQuery.isLoading && !statusCounts ? (
                  <Skeleton className='h-8 w-20' />
                ) : (
                  <div className='text-3xl font-bold text-brand-navy'>{pendingCount}</div>
                )}
                <p className='text-xs text-brand-gray mt-1'>Awaiting action</p>
              </CardContent>
            </Card>
            {isRfcAdmin && (
              <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                    CBS Queue
                    <AlertTriangle className='h-5 w-5 text-amber' />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {rfcsQuery.isLoading && !statusCounts ? (
                    <Skeleton className='h-8 w-20' />
                  ) : (
                    <div className='text-3xl font-bold text-amber'>{cbsQueueCount}</div>
                  )}
                  <p className='text-xs text-brand-gray mt-1'>Needs group creation</p>
                </CardContent>
              </Card>
            )}
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                  {isBrokerUser ? 'In Progress' : 'In Processing'}
                  <RefreshCw className='h-5 w-5 text-brand-blue' />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rfcsQuery.isLoading && !statusCounts ? (
                  <Skeleton className='h-8 w-20' />
                ) : (
                  <div className='text-3xl font-bold text-brand-navy'>{processingCount}</div>
                )}
                <p className='text-xs text-brand-gray mt-1'>Being processed</p>
              </CardContent>
            </Card>
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                  Completed
                  <CheckCircle className='h-5 w-5 text-emerald' />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rfcsQuery.isLoading && !statusCounts ? (
                  <Skeleton className='h-8 w-20' />
                ) : (
                  <div className='text-3xl font-bold text-brand-navy'>
                    {statusCounts?.COMPLETED || 0}
                  </div>
                )}
                <p className='text-xs text-brand-gray mt-1'>Successfully completed</p>
              </CardContent>
            </Card>
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                  Total Deals
                  <FileText className='h-5 w-5 text-brand-gray' />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rfcsQuery.isLoading && !pagination ? (
                  <Skeleton className='h-8 w-20' />
                ) : (
                  <div className='text-3xl font-bold text-brand-navy'>{pagination?.total || 0}</div>
                )}
                <p className='text-xs text-brand-gray mt-1'>All submissions</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters — shared by the table and kanban views, so they live
              outside the DataTable toolbar but write into its state. */}
          <Card className='border-gray-200 shadow-sm mb-6'>
            <CardContent className='pt-6'>
              <div className='flex flex-col md:flex-row gap-4'>
                <div className='flex-1 relative'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                  <Input
                    placeholder='Search by company name...'
                    value={table.search}
                    onChange={(e) => table.setSearch(e.target.value)}
                    className='pl-10 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                  />
                </div>
                <div className='w-full md:w-48'>
                  <Select
                    value={statusFilter || 'ALL'}
                    onValueChange={(value) =>
                      table.setFilter('status', value === 'ALL' ? '' : value)
                    }
                  >
                    <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                      <SelectValue placeholder='All Statuses' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='ALL'>All Statuses</SelectItem>
                      <SelectItem value={ACTION_REQUIRED}>Action Required</SelectItem>
                      {FILTERABLE_STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>
                          {getRfcStatusDisplayName(status)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isInternalUser && (
                  <div className='w-full md:w-48'>
                    <Select
                      value={clientFilter || 'ALL'}
                      onValueChange={(value) =>
                        table.setFilter('client', value === 'ALL' ? '' : value)
                      }
                    >
                      <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                        <SelectValue placeholder='All Clients' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='ALL'>All Clients</SelectItem>
                        {clientsQuery.data?.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <ExportCsvButton refetch={exportCsvQuery.refetch} filenamePrefix='deals' />
              </div>
            </CardContent>
          </Card>

          {/* Table View */}
          {effectiveViewMode === 'table' ? (
            <>
              <DataTable<RfcRow, Filters>
                table={table}
                columns={columns}
                data={rfcsQuery.data?.data}
                total={pagination?.total ?? 0}
                totalPages={pagination?.totalPages ?? 0}
                loading={rfcsQuery.isLoading}
                error={rfcsQuery.error}
                emptyMessage={
                  hasActiveFilters
                    ? 'No deals found — try adjusting your search or filters'
                    : 'No deals found — get started by creating your first deal'
                }
                title='Deal Submissions'
                entity={{singular: 'deal', plural: 'deals'}}
                rowKey={(rfc) => rfc.id}
              />
              {!rfcsQuery.isLoading &&
                !rfcsQuery.error &&
                rfcData.length === 0 &&
                !hasActiveFilters && (
                  <div className='text-center mt-4'>
                    <Button
                      asChild
                      className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                    >
                      <Link to={createUrl}>
                        <Plus className='h-4 w-4 mr-2' />
                        Create First Deal
                      </Link>
                    </Button>
                  </div>
                )}
            </>
          ) : (
            /* Kanban View */
            <div className='-mx-2'>
              <div className='flex gap-4 overflow-x-auto px-2 pb-2'>
                {KANBAN_STATUSES.map((status) => {
                  const items = groupedByStatus[status] || [];
                  // "Action Required" is a synthetic multi-status filter, so
                  // match against its status set rather than direct equality.
                  if (statusFilter === ACTION_REQUIRED) {
                    if (!actionRequiredStatuses.includes(status)) return null;
                  } else if (statusFilter && status !== statusFilter) {
                    return null;
                  }
                  return (
                    <div key={status} className='min-w-[320px] w-[320px]'>
                      <Card className='h-full border-gray-200 shadow-sm'>
                        <CardHeader className='py-3 bg-gray-50 border-b'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-2'>
                              {getStatusIcon(status)}
                              <CardTitle className='text-base font-semibold text-brand-navy'>
                                {getRfcStatusDisplayName(status)}
                              </CardTitle>
                            </div>
                            <Badge variant={getStatusColor(status)}>{items.length}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className='space-y-3 p-4'>
                          {items.length === 0 ? (
                            <div className='text-sm text-brand-gray py-6 text-center'>No items</div>
                          ) : (
                            items.map((rfc) => (
                              <div
                                key={rfc.id}
                                className='border border-gray-200 rounded-md p-3 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer'
                                onClick={() => navigate(getDetailUrl(rfc))}
                              >
                                <div className='font-medium leading-snug text-brand-navy'>
                                  {getCompanyName(rfc)}
                                </div>
                                {isInternalUser && (
                                  <div className='text-xs text-brand-gray mt-1'>
                                    {rfc.client.name}
                                  </div>
                                )}
                                <div className='flex items-center justify-between mt-3'>
                                  <div className='flex items-center gap-2 text-xs text-brand-gray'>
                                    <Calendar className='h-3 w-3' />
                                    <span>
                                      {rfc.submittedAt
                                        ? format(new Date(rfc.submittedAt), 'MMM d, yyyy')
                                        : 'Not submitted'}
                                    </span>
                                  </div>
                                  {isInternalUser && (
                                    <span className='text-xs text-brand-gray'>
                                      {rfc.attachments.length} files
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
              {!!pagination && pagination.totalPages > 1 && (
                <div className='flex items-center justify-between mt-4'>
                  <p className='text-sm text-brand-gray'>
                    Showing {(table.page - 1) * pageSize + 1} to{' '}
                    {Math.min(table.page * pageSize, pagination.total)} of {pagination.total}{' '}
                    results
                  </p>
                  <div className='flex space-x-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => table.setPage(table.page - 1)}
                      disabled={!pagination.hasPrev}
                      className='transition-colors'
                    >
                      Previous
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => table.setPage(table.page + 1)}
                      disabled={!pagination.hasNext}
                      className='transition-colors'
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
